import { Plus, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { JobPostingsTable } from '@/components/job-postings/JobPostingsTable';
import { useJobPostingFilters } from '@/hooks/useJobPostingFilters';
import { emptyToUndefined } from '@/lib/utils';
import { getJobPostings } from '@/services/jobPostingService';

import type { JobPosting } from '@/types/jobPosting.types';
import type { PaginationMeta } from '@/types/submission.types';

export function JobPostingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filters = useJobPostingFilters();

  const [postings, setPostings] = useState<JobPosting[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setError(null);

    getJobPostings({
      q: emptyToUndefined(filters.debouncedSearch),
      status: emptyToUndefined(filters.status),
      role: emptyToUndefined(filters.role),
      siteState: emptyToUndefined(filters.siteState),
      from: emptyToUndefined(filters.dateFrom),
      to: emptyToUndefined(filters.dateTo),
      sortBy: emptyToUndefined(filters.sortBy) as
        | 'createdAt'
        | 'title'
        | 'postingNumber'
        | 'status'
        | 'role'
        | undefined,
      sortOrder: filters.sortBy ? filters.sortOrder : undefined,
      page: filters.page,
    })
      .then((data) => {
        if (!isCancelled) {
          setPostings(data.items);
          setMeta(data.meta);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : t('jobPostings.errorLoading'));
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    filters.debouncedSearch,
    filters.status,
    filters.role,
    filters.siteState,
    filters.dateFrom,
    filters.dateTo,
    filters.sortBy,
    filters.sortOrder,
    filters.page,
    t,
  ]);

  function handleRowClick(posting: JobPosting) {
    void navigate(`/job-postings/${posting.jobPostingId}`);
  }

  const selectClassName =
    'rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none';

  return (
    <div className='space-y-6'>
      {/* Filters */}
      <div className='space-y-3'>
        <div className='flex flex-col md:flex-row gap-3'>
          <div className='relative flex-1'>
            <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' />
            <input
              type='text'
              value={filters.searchInput}
              onChange={(e) => filters.setSearchInput(e.target.value)}
              placeholder={t('jobPostings.searchPlaceholder')}
              className='w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none'
            />
          </div>

          <div className='flex flex-wrap gap-2'>
            <select
              value={filters.status}
              onChange={(e) => filters.setStatus(e.target.value)}
              className={selectClassName}>
              <option value=''>{t('jobPostings.filters.allStatuses')}</option>
              <option value='draft'>{t('jobPostings.statuses.draft')}</option>
              <option value='active'>{t('jobPostings.statuses.active')}</option>
              <option value='filled'>{t('jobPostings.statuses.filled')}</option>
              <option value='closed'>{t('jobPostings.statuses.closed')}</option>
            </select>

            <select value={filters.role} onChange={(e) => filters.setRole(e.target.value)} className={selectClassName}>
              <option value=''>{t('jobPostings.filters.allRoles')}</option>
              <option value='armed'>{t('jobPostings.roles.armed')}</option>
              <option value='unarmed'>{t('jobPostings.roles.unarmed')}</option>
              <option value='supervisor'>{t('jobPostings.roles.supervisor')}</option>
              <option value='patrol'>{t('jobPostings.roles.patrol')}</option>
              <option value='event'>{t('jobPostings.roles.event')}</option>
            </select>

            <select
              value={filters.siteState}
              onChange={(e) => filters.setSiteState(e.target.value)}
              className={selectClassName}>
              <option value=''>{t('jobPostings.filters.allStates')}</option>
              <option value='FL'>{t('jobPostings.siteStates.FL')}</option>
              <option value='NY'>{t('jobPostings.siteStates.NY')}</option>
            </select>
          </div>
        </div>

        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
          <div className='flex flex-wrap items-center gap-3'>
            <span className='text-sm text-gray-500'>{t('jobPostings.filters.dateFrom')}</span>
            <input
              type='date'
              value={filters.dateFrom}
              onChange={(e) => filters.setDateFrom(e.target.value)}
              className='rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none'
              aria-label={t('jobPostings.filters.dateFrom')}
            />
            <span className='text-sm text-gray-500'>{t('jobPostings.filters.dateTo')}</span>
            <input
              type='date'
              value={filters.dateTo}
              onChange={(e) => filters.setDateTo(e.target.value)}
              className='rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none'
              aria-label={t('jobPostings.filters.dateTo')}
            />
          </div>

          <div className='flex items-center gap-2 ml-auto'>
            {filters.activeFilterCount > 0 && (
              <button
                type='button'
                onClick={filters.clearAll}
                className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors'>
                <X size={14} />
                {t('jobPostings.filters.clearAll')}
                <span className='inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-gray-200 text-xs font-medium'>
                  {filters.activeFilterCount}
                </span>
              </button>
            )}

            <Link
              to='/job-postings/create'
              className='inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 transition'>
              <Plus size={16} />
              {t('jobPostings.createNew')}
            </Link>
          </div>
        </div>
      </div>

      {error ? (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>
      ) : (
        <JobPostingsTable
          postings={postings}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={filters.handleSort}
          meta={meta}
          onPageChange={filters.setPage}
        />
      )}
    </div>
  );
}
